import type { KeycloakClientRole } from '../../auth/clientRoles';
import type { CurrentUserProfile } from '../../types/user';
import { isValidEmailFormat } from '../../utils/formUtils';
import {
  dateInputToUnixMillis,
  unixMillisToDateInput,
} from '../../utils/dateTimeUtils';

export function isTenantUserDetailsDirty(options: {
  profile: CurrentUserProfile;
  email: string;
  firstName: string;
  lastName: string;
  validFrom: string;
  validTo: string;
  roleOverride: KeycloakClientRole | null;
  loadedRole: KeycloakClientRole;
  photoChanged: boolean;
  photoError: string | null;
}): boolean {
  const {
    profile,
    email,
    firstName,
    lastName,
    validFrom,
    validTo,
    roleOverride,
    loadedRole,
    photoChanged,
    photoError,
  } = options;

  return (
    email.trim() !== (profile.email ?? '').trim() ||
    firstName.trim() !== (profile.firstName ?? '').trim() ||
    lastName.trim() !== (profile.lastName ?? '').trim() ||
    validFrom !== unixMillisToDateInput(profile.validFrom) ||
    validTo !== unixMillisToDateInput(profile.validTo) ||
    (roleOverride != null && roleOverride !== loadedRole) ||
    photoChanged ||
    photoError != null
  );
}

export function isTenantUserDetailsSaveDisabled(options: {
  isDirty: boolean;
  isBusy: boolean;
  isRoleLoading: boolean;
  isRoleLoadError: boolean;
  email: string;
  username: string;
  firstName: string;
  lastName: string;
  validFrom: string;
  validTo: string;
  photoError: string | null;
}): boolean {
  const {
    isDirty,
    isBusy,
    isRoleLoading,
    isRoleLoadError,
    email,
    username,
    firstName,
    lastName,
    validFrom,
    validTo,
    photoError,
  } = options;

  const trimmedEmail = email.trim();
  const isEmailInvalid =
    trimmedEmail !== '' && !isValidEmailFormat(trimmedEmail);
  const validFromMillis = dateInputToUnixMillis(validFrom);
  const validToMillis = dateInputToUnixMillis(validTo);
  const isValidToBeforeValidFrom =
    validFromMillis != null &&
    validToMillis != null &&
    validToMillis < validFromMillis;

  return (
    !isDirty ||
    isBusy ||
    isRoleLoading ||
    isRoleLoadError ||
    trimmedEmail === '' ||
    isEmailInvalid ||
    username.trim() === '' ||
    isValidToBeforeValidFrom ||
    firstName.trim() === '' ||
    lastName.trim() === '' ||
    photoError != null
  );
}

export function isTenantUserValidToBeforeValidFrom(
  validFrom: string,
  validTo: string,
): boolean {
  const validFromMillis = dateInputToUnixMillis(validFrom);
  const validToMillis = dateInputToUnixMillis(validTo);
  return (
    validFromMillis != null &&
    validToMillis != null &&
    validToMillis < validFromMillis
  );
}
